import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';

import { ajvResolver } from '@hookform/resolvers/ajv';
import { Info as InfoIcon } from '@mui/icons-material';
import { Stack, Switch, Typography } from '@mui/material';
import { JSONSchemaType } from 'ajv';
import { useAtomValue } from 'jotai';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';

import { userInfoAtom } from '~atoms';
import { AsyncTextField, FormContainer, TextField, TransferList } from '~components';
import { useAuthorize } from '~features/authentication';
import { NfcTag, SkcKeysService } from '~features/nfc';
import { Organisation, OrganisationAutocomplete } from '~features/organisations';
import { User, UsersService } from '~features/users';
import { useFormContainerState } from '~hooks';
import { FormWrapperRefProps } from '~interfaces/refProps';

import schema from './skcUserGroupSchema.json';
import { SkcUserGroupNew } from '../../interfaces/skcUserGroup';
import SkcUserGroupsService from '../../services/skcUserGroupsService';

enum ParticipantTypeEnum {
	NfcTag = 'nfcTag',
	User = 'user',
}

const userGroupsService = new SkcUserGroupsService();
const skcKeysService = new SkcKeysService();
const usersService = new UsersService();

interface SkcUserGroupFormProps {
	id?: string;
	onSubmit?: (value: SkcUserGroupNew) => void;
}

const SkcUserGroupForm = forwardRef<FormWrapperRefProps, SkcUserGroupFormProps>(
	({ id, onSubmit }, ref) => {
		const { t } = useTranslation();

		const { isSuperAdmin } = useAuthorize();
		const userInfo = useAtomValue(userInfoAtom);

		const editMode: boolean = id != null;

		const [selectionType, setSelectionType] = useState<ParticipantTypeEnum>(
			ParticipantTypeEnum.NfcTag
		);

		const [rightPage, setRightPage] = useState(1);
		const [rowsPerPage, setRowsPerPage] = useState(5);

		const [totalLeftCount, setTotalLeftCount] = useState<number>(0);
		const [availableLeftList, setAvailableLeftList] = useState([]);
		const [leftPagination, setLeftPagination] = useState({
			page: 1,
			pageSize: rowsPerPage,
		});

		const [searchQuery, setSearchQuery] = useState<string>('');

		const { data: userGroupData, isLoading: isUserGroupLoading } = useSWRImmutable(
			id ? [userGroupsService.basePath, id] : null,
			([_, args]) => userGroupsService.getUserGroupById(args),
			{
				revalidateOnMount: true,
			}
		);

		const { setDisabled } = useFormContainerState();
		const { getValues, control, formState, reset, watch, setValue } = useForm<SkcUserGroupNew>({
			defaultValues: useMemo<Partial<SkcUserGroupNew>>(
				() =>
					userGroupData ?? {
						label: '',
						description: '',
						organisation: isSuperAdmin() ? undefined : userInfo.organisation,
					},
				[userGroupData]
			),
			mode: 'onChange',
			resolver: ajvResolver(schema as JSONSchemaType<any>),
		});

		const watchOrganisation = watch('organisation');

		const nfcTagsFetchParameters = useMemo(
			() => ({
				...leftPagination,
				organisationId: watchOrganisation?.id,
				searchQuery: searchQuery,
				includeUsers: false,
			}),
			[leftPagination, watchOrganisation, searchQuery]
		);

		const usersFetchParameters = useMemo(
			() => ({
				...leftPagination,
				organisationId: watchOrganisation?.id,
				searchQuery: searchQuery,
				excludeAnonymous: true,
			}),
			[leftPagination, watchOrganisation, searchQuery]
		);

		const {
			data: nfcTagsData,
			isLoading: isNfcTagsLoading,
			isValidating: isNfcTagsValidating,
			error: nfcTagsError,
		} = useSWR(
			selectionType === ParticipantTypeEnum.NfcTag && [
				skcKeysService.basePath,
				nfcTagsFetchParameters,
			],
			([_, args]) => skcKeysService.getKeys(args),
			{
				onSuccess: (res) => setTotalLeftCount(res.total),
			}
		);

		const {
			data: usersData,
			isLoading: isUsersLoading,
			isValidating: isUsersValidating,
			error: usersError,
		} = useSWR(
			selectionType === ParticipantTypeEnum.User && [usersService.basePath, usersFetchParameters],
			([_, args]) => usersService.getUsers(args),
			{
				onSuccess: (res) => setTotalLeftCount(res.total),
			}
		);

		const leftData = nfcTagsData || usersData;
		const isLeftLoading = isNfcTagsLoading || isUsersLoading;
		const isLeftValidating = isNfcTagsValidating || isUsersLoading;
		const leftError = nfcTagsError || usersError;

		useImperativeHandle(
			ref,
			() => ({
				onSubmit: () => onSubmit?.(getValues()),
			}),
			[onSubmit]
		);

		useEffect(() => {
			if (userGroupData) {
				// Used to reset the useform, otherwise the page won't properly reload
				reset(userGroupData);
			}
		}, [reset, userGroupData]);

		useEffect(() => {
			if (!leftData) {
				setAvailableLeftList([]);
				return;
			}
		}, [leftData]);

		useEffect(() => {
			setDisabled?.(!formState.isValid);
		}, [formState.isValid, setDisabled]);

		useEffect(() => {
			if (!leftData) {
				setAvailableLeftList([]);
				return;
			}

			determineAvailableLeftItems();
		}, [leftData]);

		const handleRowsPerPageChange = (val) => {
			setRowsPerPage(val);
			setLeftPagination((prev) => ({ ...prev, pageSize: val }));
		};

		const mapNfcTagToTransferListItem = (element: NfcTag) => ({
			key: element.id,
			primary: element.label,
			secondary: `${element.id}${element.tagNumber ? ` - ${element.tagNumber}` : ''}`,
			hidden: element.hidden,
			target: {
				type: ParticipantTypeEnum.NfcTag,
				value: element,
			},
		});

		const mapUserToTransferListItem = (element: User) => ({
			key: element.id,
			primary: element.label,
			secondary: `${element.id}${element.email ? ` - ${element.email}` : ''}`,
			hidden: element.hidden,
			target: {
				type: ParticipantTypeEnum.User,
				value: element,
			},
		});

		const handleTransferListChange = (val, onChange: () => void) => {
			// TODO: should look at this. Could be no change and still trigger an
			// onChange
			const uniqueUsers: User[] = [
				...new Map(
					val.rightItems
						.filter((el) => el.target.type === ParticipantTypeEnum.User)
						.map((el) => [el?.key, el.target.value])
				).values(),
			];
			const uniqueNfcTags: NfcTag[] = [
				...new Map(
					val.rightItems
						.filter((el) => el.target.type === ParticipantTypeEnum.NfcTag)
						.map((el) => [el?.key, el.target.value])
				).values(),
			];

			const newValue = { nfcTags: uniqueNfcTags, users: uniqueUsers };
			onChange(newValue);

			determineAvailableLeftItems();
		};

		const determineAvailableLeftItems = () => {
			const participants = getValues().participants;
			const chosenArray =
				(selectionType === ParticipantTypeEnum.NfcTag ?
					participants?.nfcTags
				:	participants?.users
				)?.map((el) => el.id) ?? [];
			if (nfcTagsData) {
				const mapped = nfcTagsData.results.map((el) => ({
					...mapNfcTagToTransferListItem(el),
					disabled: chosenArray.includes(el.id),
				}));
				setAvailableLeftList(mapped);
			} else if (usersData) {
				const mapped = usersData.results.map((el) => ({
					...mapUserToTransferListItem(el),
					disabled: chosenArray.includes(el.id),
				}));
				setAvailableLeftList(mapped);
			}
		};

		const getRightList = (value) => {
			const searchQueryLowerCase = searchQuery.toLowerCase();

			const nfcTags = value?.nfcTags?.map((el) => ({
				...el,
				hidden: !(
					el.label?.toLowerCase().includes(searchQueryLowerCase) ||
					el.tagNumber?.toLowerCase().includes(searchQueryLowerCase)
				),
			}));

			const users =
				searchQueryLowerCase !== '' ?
					value?.users?.map((el) => ({
						...el,
						hidden: !el.label?.toLowerCase().includes(searchQueryLowerCase),
					}))
				:	value?.users;

			const items = [
				...(nfcTags?.map((el) => mapNfcTagToTransferListItem(el)) ?? []),
				...(users?.map((el) => mapUserToTransferListItem(el)) ?? []),
			];
			return items;
		};

		/**
		 * Do this through a useEffect? A bit more stable if we change
		 * the organisation programmatically
		 * @param newOrganisation
		 * @param onChange
		 */
		const handleOrgansationChange = (
			newOrganisation: Organisation,
			onChange?: (...event: any[]) => void
		) => {
			const previousOrganisation: Organisation | undefined = getValues('organisation');
			if (previousOrganisation?.id !== newOrganisation.id && !editMode) {
				setValue('participants', { users: [], nfcTags: [] });
			}

			onChange?.(newOrganisation);
		};

		const handleSearchChange = (value: string) => {
			setRightPage(1);
			setSearchQuery(value);
		};

		return (
			<FormContainer loading={isUserGroupLoading}>
				{isSuperAdmin() && !editMode && (
					<Controller
						name='organisation'
						control={control}
						render={({ field }) => (
							<OrganisationAutocomplete
								required
								value={field.value}
								onChange={(e, newValue, reason) =>
									reason !== 'clear' && handleOrgansationChange(newValue, field.onChange)
								}
							/>
						)}
					/>
				)}
				<Controller
					name='label'
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							label={t('ui.label.name')}
							required
							slotProps={{
								htmlInput: {
									minLength: schema.properties.label.minLength,
									maxLength: schema.properties.label.maxLength,
								},
							}}
						/>
					)}
				/>
				<Controller
					name='description'
					control={control}
					render={({ field }) => (
						<TextField
							{...field}
							label={t('ui.label.description')}
							slotProps={{
								htmlInput: {
									maxLength: schema.properties.description.maxLength,
								},
							}}
						/>
					)}
				/>
				{watchOrganisation == null && isSuperAdmin() ?
					<Stack
						direction='row'
						spacing={1}
						sx={{ pt: '2rem', justifyContent: 'center', alignItems: 'center' }}
					>
						<InfoIcon />
						<Typography>Select an organisation before you add users or tags</Typography>
					</Stack>
				:	<>
						<Stack
							direction='row'
							spacing={1}
							sx={{
								width: 'fit-content',
							}}
						>
							<AsyncTextField
								placeholder={t('views.usersAndOrganisations.users.searchPlaceholder')}
								defaultValue={searchQuery}
								onChange={(e) => handleSearchChange(e.target.value)}
								manual
							/>
							<Stack
								direction='row'
								spacing={1}
								sx={{
									alignItems: 'center',
								}}
							>
								<Typography>{t('ui.label.users')}</Typography>
								<Switch
									checked={selectionType === ParticipantTypeEnum.NfcTag}
									onChange={(e) =>
										setSelectionType(
											e.target.checked ? ParticipantTypeEnum.NfcTag : ParticipantTypeEnum.User
										)
									}
								/>
								<Typography>{t('ui.label.nfcTags')}</Typography>
							</Stack>
						</Stack>
						<Controller
							name='participants'
							control={control}
							render={({ field }) => (
								<TransferList
									leftTitle={t('availableResource', {
										resource: t(selectionType === ParticipantTypeEnum.NfcTag ? 'tags' : 'users'),
									})}
									leftItems={availableLeftList}
									leftItemsCount={totalLeftCount}
									leftLoading={isLeftLoading || isLeftValidating}
									leftPage={leftPagination.page}
									onLeftPageChange={(val) => setLeftPagination((prev) => ({ ...prev, page: val }))}
									rowsPerPage={rowsPerPage}
									rightTitle={t('selectedResource', {
										resource: `${t(`tags`)} & ${t('users')}`,
										interpolation: { escapeValue: false },
									})}
									rightItems={getRightList(field.value)}
									rightPage={rightPage}
									onRightPageChange={(val) => setRightPage(val)}
									onChange={(val) => handleTransferListChange(val, field.onChange)}
									onRowsPerPageChange={handleRowsPerPageChange}
								/>
							)}
						/>
					</>
				}
			</FormContainer>
		);
	}
);
SkcUserGroupForm.displayName = 'SkcUserGroupForm';

export default SkcUserGroupForm;
